import { useKeyboardController, useMidiController, useStudio } from "@/contexts/studio-context";
import { useEffect, useRef } from "react";
import { armSplashFallback, hideNativeSplash } from "@/lib/native/splash-screen";

type PianoViewport2Props = {
  /**
   * Autorise la création du canvas Pixi.
   *
   * `StudioPage` ne la donne qu'à la première venue sur le studio, et ne la
   * reprend jamais : voir le verrou `canvasBooted` là-bas pour le pourquoi.
   * Tant qu'elle est fausse, ce composant fait tout le reste de son travail —
   * piste piano, MIDI, clavier — mais n'ouvre aucun contexte de rendu.
   */
  readonly bootCanvas: boolean;
};

export const PianoViewport2 = ({ bootCanvas }: PianoViewport2Props) => {
  const { studio } = useStudio();
  const midi = useMidiController();
  const keyboard = useKeyboardController();
  const containerRef = useRef<HTMLDivElement>(null);
  const viewStartedRef = useRef(false);

  // Le MIDI et le clavier sont montés dès l'arrivée du composant, quelle que
  // soit la route : ils ne coûtent rien.
  //
  // La piste piano, elle, n'est plus montée ici. Elle appartient à la session
  // en scène, qui la crée en entrant : c'est ce qui fait que ce qu'on joue en
  // séance ne suit pas dans la session solo suivante — et, accessoirement, ce
  // qui a supprimé le « Piano track already exists » que provoquait un
  // remontage de ce composant pendant un démontage asynchrone.
  useEffect(() => {
    if (!studio || !midi || !keyboard) {
      return;
    }

    midi.attach();
    keyboard.setScoped(true);

    return () => {
      // Keyboard listening is scoped to this viewport, the controller itself
      // is owned by the StudioProvider. Drop the scope flag so global key
      // events stop firing into the piano once the viewport is gone.
      keyboard.setScoped(false);
    };
  }, [studio, midi, keyboard]);

  /**
   * Le splash natif, quand aucun rendu ne se prépare derrière.
   *
   * Il était masqué par le seul `createView`. Déporter le démarrage du canvas
   * hors des routes studio l'aurait donc laissé sur un lancement natif, qui
   * ouvre `/` — une route où le canvas ne démarre plus. Le premier écran est
   * monté à ce moment-là : il n'y a plus rien à attendre.
   */
  useEffect(() => {
    if (bootCanvas) {
      return;
    }

    void hideNativeSplash();
  }, [bootCanvas]);

  useEffect(() => {
    if (!studio || !bootCanvas) {
      return;
    }

    const mountNode = containerRef.current;

    if (!mountNode) {
      return;
    }

    // Le canvas ne se recrée jamais : `studio.createView` construit une
    // application Pixi entière, et rien ici ne la démonte.
    if (viewStartedRef.current) {
      return;
    }

    viewStartedRef.current = true;

    // Arm the safety-net before we kick off the async Pixi boot, so a stalled
    // `createView` never leaves the native splash stuck on screen.
    armSplashFallback();

    studio.createView(mountNode).then(() => {
      void hideNativeSplash();
    });
  }, [studio, bootCanvas]);

  return <div ref={containerRef} className="w-full h-full" />;
};
